import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import api from '../services/api';

interface AppBranding {
  applicationName: string;
  applicationLogo: string;
  applicationLogoDark: string;
}

interface SettingsContextType {
  branding: AppBranding;
  loading: boolean;
  refreshBranding: () => Promise<void>;
}

const defaultBranding: AppBranding = {
  applicationName: 'VulnScanner',
  applicationLogo: '',
  applicationLogoDark: '',
};

const SettingsContext = createContext<SettingsContextType>({
  branding: defaultBranding,
  loading: true,
  refreshBranding: async () => {},
});

export const useSettings = () => useContext(SettingsContext);

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [branding, setBranding] = useState<AppBranding>(defaultBranding);
  const [loading, setLoading] = useState(true);

  const fetchBranding = useCallback(async () => {
    try {
      // Use public branding endpoint (no auth required)
      const response = await api.get('/branding');
      if (response.data) {
        setBranding({
          applicationName: response.data.application_name || 'VulnScanner',
          applicationLogo: response.data.application_logo || '',
          applicationLogoDark: response.data.application_logo_dark || '',
        });
      }
    } catch (error) {
      // If error, use defaults silently (don't log to console)
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshBranding = useCallback(async () => {
    setLoading(true);
    await fetchBranding();
  }, [fetchBranding]);

  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  const value = useMemo(() => ({
    branding, loading, refreshBranding,
  }), [branding, loading, refreshBranding]);

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export default SettingsContext;
